// Pure logic for the case Activity Timeline strip.
// No React, no DOM — everything here is deterministic and unit-testable.

import dayjs from "dayjs";
import {
  BuildTimelineOptions,
  TimelineModel,
  TimelinePoint,
  TimelineStats,
  TimelineTick,
  TouchWeight,
} from "./types";

/* ------------------------------------------------------------------ *
 * Activity type classification
 * ------------------------------------------------------------------ */

/**
 * Lifecycle / admin events. Rendered as small hollow markers and never
 * counted as touches.
 */
const SYSTEM_TYPES: ReadonlySet<string> = new Set([
  "Case Created",
  "Case Closed",
  "Case Reopened",
  "Case Merged",
  "BFC Reassigned",
  "Secondary BFC Assigned",
  "Unassign Secondary BFC",
  "Web Intake Assigned",
  "Delete File",
  "User Offboarded",
  "Edit Firm",
]);

/**
 * Real human-to-client contact or a milestone on the case.
 * (The free-text interaction types from `Case/Interaction.tsx` are logged with
 * their interaction type as the activity type, so they live here too.)
 */
const SUBSTANTIVE_TYPES: ReadonlySet<string> = new Set([
  // case flow milestones
  "Initial Contact",
  "Business Assessment",
  "Discovery Questions",
  // referrals
  "Referral",
  "Referral Notice",
  "B2B Referral",
  "Provider Referral Initiated",
  "Provider Referral Notice Email",
  // direct contact
  "Email Communication",
  "Event Invitation",
  // impact
  "Outcome Recorded",
  // logged interactions (Interaction.tsx `interaction_types`)
  "Attempted Contact",
  "B2B Approval",
  "B2B Prerequisites",
  "B2B Request Incomplete/Denied",
  "Client Interaction",
  "Community Interaction",
  "Follow-up",
  "Scheduled Visit",
]);

/** Internal work on the case — counts as a touch, but a light one. */
const LIGHT_TYPES: ReadonlySet<string> = new Set([
  "Upload File",
  "Action Item",
  "Action Item Task Completed",
  "Action Item Task Reopened",
  "Reminder",
  "Reminder (completed)",
  "Referral Status Updated",
  "Referral Survey Email",
  "Client Referral Survey",
  "B2B Client Survey Email",
  "B2B Provider Survey Email",
  "Providers Referral Email",
  "Event Survey",
  "Event Survey Email",
]);

/**
 * Survey *responses* ("… (by Client)" / "… (by Provider)") are substantive —
 * the client or the provider actually answered.
 */
const RESPONSE_SUFFIXES = ["(by Client)", "(by Provider)"];

/**
 * Classify an activity type into a touch weight.
 * Unknown types fall through to "system" so a new backend type can never
 * inflate the touch counts by accident.
 */
export const classifyActivity = (activityType: string): TouchWeight => {
  const type = (activityType || "").trim();
  if (!type) return "system";
  if (SYSTEM_TYPES.has(type)) return "system";
  if (SUBSTANTIVE_TYPES.has(type)) return "substantive";
  if (LIGHT_TYPES.has(type)) return "light";
  if (RESPONSE_SUFFIXES.some((suffix) => type.endsWith(suffix))) {
    return "substantive";
  }
  // "(edit)" variants of anything are bookkeeping, not a new touch.
  if (type.endsWith("(edit)")) return "light";
  return "system";
};

/** Activity types the Case page does not open a modal for. */
export const IGNORED_ACTIVITY_TYPES: string[] = [
  "Case Created",
  "Secondary BFC Assigned",
  "Unassign Secondary BFC",
  "Referral",
  "Case Merged",
  "Case Closed",
  "Web Intake Assigned",
  "BFC Reassigned",
];

const IGNORED_SET: ReadonlySet<string> = new Set(IGNORED_ACTIVITY_TYPES);

/** Case.tsx `caseTypeMap` — the only two renames in the product. */
export const displayLabel = (activityType: string): string => {
  if (activityType === "Initial Contact") return "Initiate Contact";
  if (activityType === "Initial Contact (edit)")
    return "Initiate Contact (edit)";
  return activityType;
};

/* ------------------------------------------------------------------ *
 * Dates
 * ------------------------------------------------------------------ */

const isUsable = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const parseDate = (value: unknown): Date | null => {
  if (!isUsable(value)) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number" || typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const INVALID_DATE = () => new Date(NaN);

/**
 * interaction_date ?? activity_date ?? created_at.
 * If the chosen value is not a real date, fall back to created_at.
 * Returns an Invalid Date when nothing usable exists (buildTimeline drops those).
 */
export const activityDate = (activity: any): Date => {
  if (!activity) return INVALID_DATE();
  const candidates = [
    activity.interaction_date,
    activity.activity_date,
    activity.created_at,
  ];
  const chosen = candidates.find(isUsable);
  const parsed = parseDate(chosen);
  if (parsed) return parsed;
  return parseDate(activity.created_at) ?? INVALID_DATE();
};

const isValidDate = (d: Date | null | undefined): d is Date =>
  !!d && !Number.isNaN(d.getTime());

/* ------------------------------------------------------------------ *
 * Timeline construction
 * ------------------------------------------------------------------ */

const DEFAULT_OVERLAP_THRESHOLD = 0.012;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Below this span we tick by quarter instead of by year. */
const QUARTER_TICK_MAX_MONTHS = 18;
/** Below this span we tick by month instead of by quarter. */
const MONTH_TICK_MAX_MONTHS = 4;
/** A boundary tick closer than this (0..1) to the start tick is dropped so
 *  the two labels never overlap. */
const MIN_TICK_GAP = 0.08;

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

const startOfYear = (d: Date): Date => new Date(d.getFullYear(), 0, 1);

/** Lane order within a swarm cluster: 0, 1, -1, 2, -2, 3, -3 … */
const laneForIndex = (index: number): number => {
  if (index <= 0) return 0;
  const magnitude = Math.ceil(index / 2);
  return index % 2 === 1 ? magnitude : -magnitude;
};

type TickUnit = "year" | "quarter" | "month";

/**
 * The axis starts at the case's first date (not a calendar boundary), so the
 * first tick is labelled with that actual month ("Apr 2018") and the
 * following ticks sit on year / quarter / month boundaries depending on the
 * span. A boundary that lands within MIN_TICK_GAP of the start is skipped.
 */
const buildTicks = (start: Date, end: Date): TimelineTick[] => {
  const span = end.getTime() - start.getTime();
  const months = dayjs(end).diff(dayjs(start), "month", true);
  const unit: TickUnit =
    months < MONTH_TICK_MAX_MONTHS
      ? "month"
      : months < QUARTER_TICK_MAX_MONTHS
        ? "quarter"
        : "year";

  const xOf = (d: Date) => clamp01((d.getTime() - start.getTime()) / span);
  const labelOf = (d: Date) => {
    if (unit === "year") return `${d.getFullYear()}`;
    if (unit === "quarter") {
      return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    }
    return dayjs(d).format("MMM YYYY");
  };

  const ticks: TimelineTick[] = [
    { date: start, x: 0, label: dayjs(start).format("MMM YYYY") },
  ];

  // First boundary strictly after start.
  const step = unit === "year" ? 12 : unit === "quarter" ? 3 : 1;
  const alignedMonth = Math.floor(start.getMonth() / step) * step;
  let cursor = new Date(start.getFullYear(), alignedMonth + step, 1);

  while (cursor.getTime() <= end.getTime()) {
    const x = xOf(cursor);
    if (x >= MIN_TICK_GAP) {
      ticks.push({ date: cursor, x, label: labelOf(cursor) });
    }
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + step, 1);
  }

  return ticks;
};

const emptyStats = (): TimelineStats => ({
  touches: 0,
  substantive: 0,
  lastTouch: null,
  lastSubstantive: null,
  firstTouch: null,
});

export const buildTimeline = (
  activities: any[],
  opts: BuildTimelineOptions = {},
): TimelineModel => {
  const overlapThreshold =
    typeof opts.overlapThreshold === "number" && opts.overlapThreshold > 0
      ? opts.overlapThreshold
      : DEFAULT_OVERLAP_THRESHOLD;

  const rows = Array.isArray(activities) ? activities : [];

  // 1. Keep the usable rows and resolve their dates once.
  const dated = rows
    .filter((a) => a && a.status !== "deleted")
    .map((a) => ({ activity: a, date: activityDate(a) }))
    .filter((r) => isValidDate(r.date));

  // 2. Window.
  const caseStart = parseDate(opts.caseStart);
  const earliest = dated.reduce<Date | null>(
    (min, r) =>
      min === null || r.date.getTime() < min.getTime() ? r.date : min,
    null,
  );

  const now = isValidDate(opts.now ?? null) ? (opts.now as Date) : new Date();

  // The axis starts exactly at the case's first date so the data uses the
  // whole strip; an empty case falls back to Jan 1 of the current year.
  let start: Date;
  if (caseStart && earliest) {
    start = caseStart.getTime() <= earliest.getTime() ? caseStart : earliest;
  } else {
    start = caseStart ?? earliest ?? startOfYear(now);
  }

  let end = now;
  if (end.getTime() <= start.getTime()) {
    end = new Date(start.getTime() + DAY_MS);
  }
  const span = end.getTime() - start.getTime();

  // 3. Points, ascending by date (id breaks ties so the order is deterministic).
  const sorted = dated.slice().sort((a, b) => {
    const delta = a.date.getTime() - b.date.getTime();
    if (delta !== 0) return delta;
    return String(a.activity._id ?? "").localeCompare(
      String(b.activity._id ?? ""),
    );
  });

  const points: TimelinePoint[] = sorted.map(({ activity, date }) => {
    const type = String(activity.activity_type ?? "");
    return {
      id: String(activity._id ?? ""),
      date,
      type,
      label: displayLabel(type),
      weight: classifyActivity(type),
      userId: activity.user_id
        ? String(activity.user_id)
        : activity.user
          ? String(activity.user)
          : undefined,
      clickable: !IGNORED_SET.has(type),
      x: clamp01((date.getTime() - start.getTime()) / span),
      lane: 0,
      cluster: 0,
      clusterSize: 1,
    };
  });

  // 4. Swarm lanes: walk left→right, cluster on distance from the cluster head.
  //    Each weight is drawn on its own row of the strip, so only points of the
  //    same weight can overlap — clustering is tracked per row. Cluster ids
  //    are still unique across rows (one global counter).
  const heads: { [weight in TouchWeight]?: { x: number; index: number; n: number } } =
    {};
  let clusterIndex = -1;
  points.forEach((p) => {
    const head = heads[p.weight];
    if (!head || p.x - head.x > overlapThreshold) {
      clusterIndex += 1;
      heads[p.weight] = { x: p.x, index: clusterIndex, n: 0 };
    } else {
      head.n += 1;
    }
    const current = heads[p.weight]!;
    p.lane = laneForIndex(current.n);
    p.cluster = current.index;
  });
  const sizes: { [cluster: number]: number } = {};
  points.forEach((p) => {
    sizes[p.cluster] = (sizes[p.cluster] || 0) + 1;
  });
  points.forEach((p) => {
    p.clusterSize = sizes[p.cluster];
  });

  // 5. Stats.
  const stats = emptyStats();
  points.forEach((p) => {
    if (p.weight === "system") return;
    stats.touches += 1;
    if (p.weight === "substantive") {
      stats.substantive += 1;
      stats.lastSubstantive = p;
    }
    if (stats.firstTouch === null) stats.firstTouch = p;
    stats.lastTouch = p;
  });

  return { start, end, points, ticks: buildTicks(start, end), stats };
};

/* ------------------------------------------------------------------ *
 * Formatting
 * ------------------------------------------------------------------ */

/** "Mar 17, 2025 · Referral - Follow Up" */
export const formatTooltip = (p: TimelinePoint): string => {
  const date = dayjs(p.date).format("MMM D, YYYY");
  const label = p.label || p.type;
  return label ? `${date} · ${label}` : date;
};

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/** "today", "yesterday", "3 days ago", "2 months ago", "in 4 days". */
export const formatRelative = (date: Date, now?: Date): string => {
  if (!isValidDate(date)) return "";
  const then = dayjs(date).startOf("day");
  const today = dayjs(
    isValidDate(now ?? null) ? (now as Date) : new Date(),
  ).startOf("day");

  const days = today.diff(then, "day");
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days === -1) return "tomorrow";

  const past = days > 0;
  const magnitude = Math.abs(days);
  let phrase: string;
  if (magnitude < 30) {
    phrase = plural(magnitude, "day");
  } else if (magnitude < 365) {
    const months = Math.max(
      1,
      Math.abs(past ? today.diff(then, "month") : then.diff(today, "month")),
    );
    phrase = plural(months, "month");
  } else {
    const years = Math.max(
      1,
      Math.abs(past ? today.diff(then, "year") : then.diff(today, "year")),
    );
    phrase = plural(years, "year");
  }
  return past ? `${phrase} ago` : `in ${phrase}`;
};
